/**
 * SQLPreview - SQL display component with syntax highlighting
 *
 * Displays SQL queries in a formatted, read-only code block
 */

import React from 'react';
import { Copy, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface SQLPreviewProps {
  sql: string;
}

export const SQLPreview: React.FC<SQLPreviewProps> = ({ sql }) => {
  const [copied, setCopied] = React.useState(false);

  /**
   * Copy SQL to clipboard
   */
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(sql);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy SQL:', error);
    }
  };

  /**
   * Simple SQL syntax highlighting
   */
  const highlightSQL = (sqlText: string): React.ReactNode => {
    const keywords = /\b(SELECT|FROM|WHERE|GROUP BY|HAVING|ORDER BY|JOIN|LEFT|RIGHT|INNER|OUTER|ON|AS|AND|OR|IN|NOT|NULL|IS|LIKE|BETWEEN|LIMIT|OFFSET|DISTINCT|COUNT|SUM|AVG|MIN|MAX)\b/gi;

    const parts = sqlText.split(/(\n)/);

    return parts.map((part, index) => {
      if (part === '\n') {
        return <br key={index} />;
      }

      const tokens = part.split(/(\s+)/);

      return (
        <span key={index}>
          {tokens.map((token, tokenIndex) => {
            if (keywords.test(token)) { // nosemgrep: rules.lgpl.nodejs_scan.javascript-dos-rule-regex_dos  -- static SQL-keyword regex tested against a single short token
              return (
                <span key={tokenIndex} className="text-info font-semibold">
                  {token}
                </span>
              );
            }
            return <span key={tokenIndex}>{token}</span>;
          })}
        </span>
      );
    });
  };

  return (
    <div className="relative group">
      {/* Copy Button */}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={handleCopy}
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </Button>

      {/* SQL Display */}
      <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm font-mono">
        <code>{highlightSQL(sql)}</code>
      </pre>
    </div>
  );
};
